/*
  selection_confirmation_modal.js
  "Controleer je selectie" op de plint-PDP. Markup: snippets/selection_confirmation_modal.liquid.
  Hooks in het thema (ongewijzigd t.o.v. de vorige versie):
    #form-confirmation-trigger            ATC-knop op mdf_plint-producten (product-form.liquid)
    .product-form--atc-button-submit      dezelfde knop, wordt programmatisch geklikt na bevestigen
    .variant-selection[variant]           gekozen variant-id of "not-selected"
    [data-variant-option-handle='kleur']  kleurgroep, data-variant-option-chosen-value
    [data-variant-option-handle='afmeting-dikte-x-hoogte' | 'formaat-dikte-x-hoogte']
    #lengthInput                          ingevoerde wandlengte (m)
    #calculator-package-amount            aantal plinten uit de rekenhulp
    #calculator-received                  geleverde meters uit de rekenhulp
  Nieuw: prijs en plintlengte per variant uit de JSON in de snippet; tekening via window.vmPlintDoorsnede.
*/
class SelectionConfirmationModal extends HTMLElement {
  constructor() {
    super();
    this._visible = false;
    this._programmaticClick = false;
    this._varianten = {};
    this._vorigeFocus = null;
    this._gegevens = null;
  }

  connectedCallback() {
    this.backdrop = this.querySelector("[data-pb-backdrop]");
    this.knopBevestig = this.querySelector("[data-pb-bevestig]");
    this.hint = this.querySelector("[data-pb-hint]");
    this.checks = Array.from(this.querySelectorAll("[data-pb-check]"));
    this.svg = this.querySelector("[data-pb-svg]");
    this.figuur = this.querySelector("[data-pb-figuur]");
    this.swatch = this.querySelector("[data-pb-swatch]");
    this.atcButton = document.querySelector(".product-form--atc-button-submit");

    try {
      const json = this.querySelector("[data-varianten]");
      this._varianten = json ? JSON.parse(json.textContent) : {};
    } catch (e) {
      this._varianten = {};
    }

    this._bind();
    this._updateKnop();
  }

  get visible() {
    return this._visible;
  }

  set visible(waarde) {
    this._visible = !!waarde;
    this.dataset.visible = this._visible ? "true" : "false";
    if (this._visible) {
      this._vorigeFocus = document.activeElement;
      this._gegevens = this._lees();
      this._render(this._gegevens);
      this.checks.forEach((c) => (c.checked = false));
      this._updateKnop();
      const eerste = this.checks[0] || this.knopBevestig || this.querySelector("[data-pb-sluit]");
      if (eerste) setTimeout(() => eerste.focus(), 30);
    } else if (this._vorigeFocus && typeof this._vorigeFocus.focus === "function") {
      this._vorigeFocus.focus();
      this._vorigeFocus = null;
    }
  }

  // ── Gebeurtenissen ────────────────────────────────────────────────
  _bind() {
    const trigger = document.querySelector("#form-confirmation-trigger");
    if (trigger) {
      trigger.addEventListener("click", (e) => {
        if (this._programmaticClick) {
          // klik vanuit de bevestigknop: gewoon toevoegen aan de winkelwagen
          this._programmaticClick = false;
          return;
        }
        // geen variant gekozen: het thema toont zelf "Kies eerst een kleur of variant"
        if (!this._variantGekozen()) return;
        e.preventDefault();
        this.visible = true;
      });
    }

    this.querySelectorAll("[data-pb-sluit]").forEach((b) => b.addEventListener("click", () => (this.visible = false)));

    if (this.backdrop) {
      this.backdrop.addEventListener("click", (e) => {
        if (e.target === this.backdrop) this.visible = false;
      });
    }

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this._visible) this.visible = false;
    });

    this.checks.forEach((c) => c.addEventListener("change", () => this._updateKnop()));

    if (this.knopBevestig) {
      this.knopBevestig.addEventListener("click", (e) => {
        e.preventDefault();
        if (this.knopBevestig.disabled) return;
        this.visible = false;
        if (!this.atcButton) this.atcButton = document.querySelector(".product-form--atc-button-submit");
        if (this.atcButton) {
          this._programmaticClick = true;
          this.atcButton.click();
        }
      });
    }
  }

  _variantGekozen() {
    const sel = document.querySelector(".variant-selection");
    return !sel || sel.getAttribute("variant") !== "not-selected";
  }

  _updateKnop() {
    const ok = this.checks.length === 0 || this.checks.every((c) => c.checked);
    if (this.knopBevestig) this.knopBevestig.disabled = !ok;
    if (this.hint) this.hint.hidden = ok;
  }

  // ── Gegevens uit de productpagina ─────────────────────────────────
  _lees() {
    const q = (s) => document.querySelector(s);
    const getal = (v) => {
      const n = parseFloat(String(v == null ? "" : v).replace(",", "."));
      return isNaN(n) ? null : n;
    };

    const kleurGroep = q("[data-variant-option-handle='kleur']");
    const kleur = kleurGroep && kleurGroep.dataset.variantOptionChosenValue ? kleurGroep.dataset.variantOptionChosenValue : null;

    const maatGroep = q("[data-variant-option-handle='afmeting-dikte-x-hoogte']") || q("[data-variant-option-handle='formaat-dikte-x-hoogte']");
    const maat = maatGroep && maatGroep.dataset.variantOptionChosenValue ? maatGroep.dataset.variantOptionChosenValue : "";
    const m = maat.match(/(\d+)\s*x\s*(\d+)/i);
    // een plint is altijd hoger dan dik, dus min = dikte en max = hoogte (MDF schrijft dikte x hoogte, folie hoogte x dikte)
    const dikte = m ? Math.min(+m[1], +m[2]) : null;
    const hoogte = m ? Math.max(+m[1], +m[2]) : null;
    // overzetplint: "18x120 mm (Met uitsparing 13x90mm)" -> uitsparing 13 mm diep, 90 mm hoog
    const u = maat.match(/uitsparing\s*(\d+)\s*x\s*(\d+)/i);
    const uitsparing = u ? { diepte: Math.min(+u[1], +u[2]), hoogte: Math.max(+u[1], +u[2]) } : null;

    const sel = q(".variant-selection");
    let variantId = sel ? sel.getAttribute("variant") : null;
    if (!variantId || variantId === "not-selected") {
      const inp = q("form[action*='/cart/add'] input[name='id'], form[action*='/cart/add'] select[name='id']");
      variantId = inp ? inp.value : null;
    }
    const variant = variantId && this._varianten[String(variantId)] ? this._varianten[String(variantId)] : null;
    const inhoud = variant && variant.inhoud > 0 ? variant.inhoud : getal(this.dataset.inhoud) || 2.4;

    const wand = getal(q("#lengthInput") ? q("#lengthInput").value : null);
    const aantalEl = q("#calculator-package-amount");
    let aantal = aantalEl ? parseInt(aantalEl.textContent, 10) : NaN;
    if (!(aantal > 0) && wand > 0) aantal = Math.ceil(wand / inhoud);
    if (!(aantal > 0)) aantal = null;
    const ontvangenEl = q("#calculator-received");
    let ontvangt = getal(ontvangenEl ? ontvangenEl.textContent : null);
    if (!(ontvangt > 0) && aantal) ontvangt = aantal * inhoud;

    return {
      kleur,
      dikte,
      hoogte,
      uitsparing,
      inhoud,
      wand: wand > 0 ? wand : null,
      aantal,
      ontvangt: ontvangt > 0 ? ontvangt : null,
      sku: variant ? variant.sku : null,
      prijs: variant ? variant.prijs : null,
      vergelijk: variant ? variant.vergelijk : null,
      // echte RAL-kleur uit de kleurnaam; de swatch in het koopblok is een variantfoto en dus niet bruikbaar
      swatch: kleur && window.vmPlintDoorsnede ? window.vmPlintDoorsnede.kleurVan(kleur) : null,
    };
  }

  // ── Weergave ──────────────────────────────────────────────────────
  _m(n) {
    return n.toFixed(2).replace(".", ",") + " m";
  }
  // mm -> cm zonder overbodige nullen: 90 -> "9", 9 -> "0,9", 115 -> "11,5"
  _cm(mm) {
    return String(Math.round(mm) / 10).replace(".", ",");
  }
  _maat(mm) {
    return mm + " mm (" + this._cm(mm) + " cm)";
  }
  _eur(n) {
    return "€ " + n.toFixed(2).replace(".", ",");
  }
  _zet(naam, tekst) {
    this.querySelectorAll("[data-pb='" + naam + "']").forEach((el) => (el.textContent = tekst == null ? "" : tekst));
  }
  _rij(naam, zichtbaar) {
    this.querySelectorAll("[data-pb-rij='" + naam + "']").forEach((el) => (el.hidden = !zichtbaar));
  }
  _blok(naam, zichtbaar) {
    this.querySelectorAll("[data-pb-blok='" + naam + "']").forEach((el) => (el.hidden = !zichtbaar));
  }

  _render(d) {
    const aantalTekst = d.aantal ? d.aantal + (d.aantal === 1 ? " plint" : " plinten") : null;

    this._zet("plintlengte", this._m(d.inhoud));
    this._zet("kleur", d.kleur);
    this._rij("kleur", !!d.kleur);
    this._zet("dikte", d.dikte ? this._maat(d.dikte) : "");
    this._rij("dikte", !!d.dikte);
    this._zet("hoogte", d.hoogte ? this._maat(d.hoogte) : "");
    this._rij("hoogte", !!d.hoogte);
    this._zet("afwerking", d.kleur && /grond/i.test(d.kleur) ? "Gegrond" : "Gelakt");
    this._zet("sku", d.sku || "");
    const skuWrap = this.querySelector("[data-pb-sku-wrap]");
    if (skuWrap) skuWrap.hidden = !d.sku;

    this._zet("wand", d.wand ? this._m(d.wand) : "");
    this._rij("wand", !!d.wand);
    this._zet("aantal", aantalTekst);
    this._rij("aantal", !!d.aantal);
    this._zet("ontvangt", d.ontvangt ? this._m(d.ontvangt) : "");
    this._rij("ontvangt", !!d.ontvangt);
    this._blok("aantal", !!(d.wand || d.aantal || d.ontvangt));

    const prijsOk = d.prijs > 0 && d.aantal > 0;
    this._blok("prijs", prijsOk);
    if (prijsOk) {
      this._zet("plintprijs", this._eur(d.prijs));
      this._zet("totaal", this._eur(d.aantal * d.prijs));
      const s = this.querySelector("[data-pb='vergelijk']");
      if (s) {
        const toon = d.vergelijk > d.prijs;
        s.hidden = !toon;
        s.textContent = toon ? this._eur(d.aantal * d.vergelijk) : "";
      }
    }

    // vinkjes: concrete waarden, met terugval als iets ontbreekt (origineel bewaren voor een volgende keer openen)
    const labelKleur = this.querySelector("[data-pb-label='kleur']");
    if (labelKleur) {
      if (!labelKleur.dataset.origineel) labelKleur.dataset.origineel = labelKleur.innerHTML;
      labelKleur.innerHTML = labelKleur.dataset.origineel;
      if (!d.kleur) labelKleur.textContent = "De kleur klopt";
      else this._zet("kleur", d.kleur);
    }
    const labelAantal = this.querySelector("[data-pb-label='aantal']");
    if (labelAantal) {
      if (!labelAantal.dataset.origineel) labelAantal.dataset.origineel = labelAantal.innerHTML;
      labelAantal.innerHTML = labelAantal.dataset.origineel;
      if (!d.aantal) labelAantal.textContent = "Het aantal plinten klopt";
      else {
        this._zet("aantal", aantalTekst);
        this._zet("plintlengte", this._m(d.inhoud));
      }
    }

    // swatch: echte kleur, of geen swatch als de RAL-naam onbekend is
    if (this.swatch) {
      this.swatch.style.display = d.swatch ? "" : "none";
      this.swatch.style.backgroundImage = "";
      this.swatch.style.backgroundColor = d.swatch || "";
    }

    // dwarsdoorsnede
    let getekend = false;
    if (this.svg && window.vmPlintDoorsnede && d.dikte && d.hoogte) {
      getekend = !!window.vmPlintDoorsnede(this.svg, {
        uitvoering: this.dataset.uitvoering,
        titel: this.dataset.titel,
        dikte: d.dikte,
        hoogte: d.hoogte,
        uitsparing: d.uitsparing,
        kleur: d.swatch,
        kleurNaam: d.kleur,
      });
    }
    if (this.figuur) {
      this.figuur.hidden = !getekend;
      const cap = this.figuur.querySelector("figcaption");
      if (cap) cap.textContent = d.uitsparing ? "Dwarsdoorsnede op schaal · uitsparing " + d.uitsparing.diepte + " × " + d.uitsparing.hoogte + " mm (" + this._cm(d.uitsparing.diepte) + " × " + this._cm(d.uitsparing.hoogte) + " cm)" : "Dwarsdoorsnede op schaal";
    }
  }
}

if (!customElements.get("selection-confirmation-modal")) {
  customElements.define("selection-confirmation-modal", SelectionConfirmationModal);
}
